import { api } from "@/api/client"
import {
  ClaimAlmanacMilestoneResponse,
  ClaimLadderStageResponse,
  HomeLadderStageKey,
  HomeMotivationStatus,
} from "../types/homeMotivation"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function pickNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

function pickString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback
}

function pickBool(value: unknown): boolean {
  return Boolean(value)
}

function normalizeTopPlayer(raw: unknown): {
  userId: number
  nickname: string
  publicGameCode: string
  localPts: number
  prizeCoins: number
  rank?: number
  globalPts?: number
} | null {
  const obj = asRecord(raw)
  if (!obj) return null
  return {
    userId: pickNumber(obj.userId ?? obj.user_id),
    nickname: pickString(obj.nickname, "—"),
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code),
    localPts: pickNumber(obj.localPts ?? obj.local_pts),
    prizeCoins: pickNumber(obj.prizeCoins ?? obj.prize_coins),
    rank: obj.rank != null ? pickNumber(obj.rank) : undefined,
    globalPts:
      obj.globalPts != null || obj.global_pts != null
        ? pickNumber(obj.globalPts ?? obj.global_pts)
        : undefined,
  }
}

export function normalizeHomeMotivation(raw: unknown): HomeMotivationStatus {
  const root = asRecord(raw) ?? {}
  const seasonObj = asRecord(root.season) ?? {}
  const almanacObj = asRecord(root.almanac) ?? {}
  const ladderObj = asRecord(root.ladder) ?? {}
  const meObj = asRecord(seasonObj.me) ?? {}
  const prizesObj = asRecord(seasonObj.prizes) ?? {}

  const milestonesRaw = Array.isArray(almanacObj.milestones)
    ? almanacObj.milestones
    : []
  const stagesRaw = Array.isArray(ladderObj.stages) ? ladderObj.stages : []
  const top3Raw = Array.isArray(seasonObj.top3) ? seasonObj.top3 : []

  return {
    season: {
      cityId: pickNumber(seasonObj.cityId ?? seasonObj.city_id),
      cityName: pickString(seasonObj.cityName ?? seasonObj.city_name, "") || null,
      seasonMonthKey: pickString(
        seasonObj.seasonMonthKey ?? seasonObj.season_month_key
      ),
      seasonTitle: pickString(seasonObj.seasonTitle ?? seasonObj.season_title),
      payoutAt: pickString(seasonObj.payoutAt ?? seasonObj.payout_at),
      daysLeft: pickNumber(seasonObj.daysLeft ?? seasonObj.days_left),
      championFundCoins: pickNumber(
        seasonObj.championFundCoins ?? seasonObj.champion_fund_coins
      ),
      minPayoutCoins: pickNumber(
        seasonObj.minPayoutCoins ?? seasonObj.min_payout_coins,
        100
      ),
      canPayout: pickBool(seasonObj.canPayout ?? seasonObj.can_payout),
      payoutShares: Array.isArray(seasonObj.payoutShares)
        ? seasonObj.payoutShares.map((v) => pickNumber(v))
        : [0.5, 0.3, 0.2],
      prizes: {
        top1: pickNumber(prizesObj.top1),
        top2: pickNumber(prizesObj.top2),
        top3: pickNumber(prizesObj.top3),
      },
      top1: normalizeTopPlayer(seasonObj.top1),
      me: {
        userId: pickNumber(meObj.userId ?? meObj.user_id),
        nickname: pickString(meObj.nickname),
        localPts: pickNumber(meObj.localPts ?? meObj.local_pts),
        rank: meObj.rank == null ? null : pickNumber(meObj.rank),
        ptsToTop1: pickNumber(meObj.ptsToTop1 ?? meObj.pts_to_top1),
        isTop1: pickBool(meObj.isTop1 ?? meObj.is_top1),
      },
      top3: top3Raw
        .map(normalizeTopPlayer)
        .filter((row): row is NonNullable<typeof row> => row != null),
    },
    almanac: {
      stampedSlots: pickNumber(
        almanacObj.stampedSlots ?? almanacObj.stamped_slots
      ),
      totalSlots: pickNumber(
        almanacObj.totalSlots ?? almanacObj.total_slots,
        108
      ),
      progressPercent: pickNumber(
        almanacObj.progressPercent ?? almanacObj.progress_percent
      ),
      milestones: milestonesRaw.map((item) => {
        const m = asRecord(item) ?? {}
        const percent = pickNumber(m.percent) as 25 | 50 | 75 | 95 | 100
        return {
          percent,
          minSlots: pickNumber(m.minSlots ?? m.min_slots),
          title: pickString(m.title),
          rewardCoins: pickNumber(m.rewardCoins ?? m.reward_coins),
          rewardChakra: pickNumber(m.rewardChakra ?? m.reward_chakra),
          rewardFuseBoosters: pickNumber(
            m.rewardFuseBoosters ?? m.reward_fuse_boosters
          ),
          frameKey: pickString(m.frameKey ?? m.frame_key),
          reached: pickBool(m.reached),
          claimed: pickBool(m.claimed),
          claimable: pickBool(m.claimable),
        }
      }),
    },
    ladder: {
      stages: stagesRaw.map((item) => {
        const s = asRecord(item) ?? {}
        const tasksRaw = Array.isArray(s.tasks) ? s.tasks : []
        return {
          key: pickString(s.key) as HomeLadderStageKey,
          title: pickString(s.title),
          order: pickNumber(s.order),
          rewardCoins: pickNumber(s.rewardCoins ?? s.reward_coins),
          rewardChakra: pickNumber(s.rewardChakra ?? s.reward_chakra),
          frameKey: pickString(s.frameKey ?? s.frame_key),
          requiredTasks: pickNumber(s.requiredTasks ?? s.required_tasks),
          totalTasks: pickNumber(s.totalTasks ?? s.total_tasks),
          doneCount: pickNumber(s.doneCount ?? s.done_count),
          tasks: tasksRaw.map((task) => {
            const t = asRecord(task) ?? {}
            return {
              key: pickString(t.key),
              title: pickString(t.title),
              done: pickBool(t.done),
              progress:
                t.progress != null ? pickNumber(t.progress) : undefined,
              target: t.target != null ? pickNumber(t.target) : undefined,
            }
          }),
          completed: pickBool(s.completed),
          claimed: pickBool(s.claimed),
          claimable: pickBool(s.claimable),
          unlocked: pickBool(s.unlocked),
        }
      }),
      currentStageKey: (pickString(
        ladderObj.currentStageKey ?? ladderObj.current_stage_key,
        ""
      ) || null) as HomeLadderStageKey | null,
      activeTitle:
        pickString(ladderObj.activeTitle ?? ladderObj.active_title, "") ||
        null,
      activeFrameKey:
        pickString(
          ladderObj.activeFrameKey ?? ladderObj.active_frame_key,
          ""
        ) || null,
      titles: Array.isArray(ladderObj.titles)
        ? ladderObj.titles.filter((t): t is string => typeof t === "string")
        : [],
    },
  }
}

export async function getHomeMotivation(): Promise<HomeMotivationStatus> {
  const { data } = await api.get<unknown>("/game/home-motivation")
  return normalizeHomeMotivation(data)
}

export async function claimAlmanacMilestone(
  percent: 25 | 50 | 75 | 95 | 100
): Promise<ClaimAlmanacMilestoneResponse> {
  const { data } = await api.post<unknown>("/game/home-motivation/almanac-milestone", {
    percent,
  })
  const obj = asRecord(data) ?? {}
  return {
    claimed: pickBool(obj.claimed),
    percent: pickNumber(obj.percent) as 25 | 50 | 75 | 95 | 100,
    title: pickString(obj.title),
    reward: {
      coins: pickNumber(asRecord(obj.reward)?.coins),
      chakra: pickNumber(asRecord(obj.reward)?.chakra),
      fuseBoosters: pickNumber(asRecord(obj.reward)?.fuseBoosters),
    },
    frameKey: pickString(obj.frameKey),
    coins: pickNumber(obj.coins),
    chakraCharges: pickNumber(obj.chakraCharges),
    fuseBoosters: pickNumber(obj.fuseBoosters),
    status: normalizeHomeMotivation(obj.status),
  }
}

export async function claimLadderStage(
  stageKey: HomeLadderStageKey
): Promise<ClaimLadderStageResponse> {
  const { data } = await api.post<unknown>("/game/home-motivation/ladder-stage", {
    stageKey,
  })
  const obj = asRecord(data) ?? {}
  return {
    claimed: pickBool(obj.claimed),
    stageKey: pickString(obj.stageKey) as HomeLadderStageKey,
    title: pickString(obj.title),
    reward: {
      coins: pickNumber(asRecord(obj.reward)?.coins),
      chakra: pickNumber(asRecord(obj.reward)?.chakra),
    },
    frameKey: pickString(obj.frameKey),
    coins: pickNumber(obj.coins),
    chakraCharges: pickNumber(obj.chakraCharges),
    status: normalizeHomeMotivation(obj.status),
  }
}

export function buildMockHomeMotivation(): HomeMotivationStatus {
  return {
    season: {
      cityId: 1,
      cityName: "Мачулищи",
      seasonMonthKey: "2026-08",
      seasonTitle: "Август 2026",
      payoutAt: new Date(Date.now() + 8 * 86400000).toISOString(),
      daysLeft: 8,
      championFundCoins: 4900,
      minPayoutCoins: 100,
      canPayout: true,
      payoutShares: [0.5, 0.3, 0.2],
      prizes: { top1: 2450, top2: 1470, top3: 980 },
      top1: {
        userId: 2,
        nickname: "ТеньСеницы",
        publicGameCode: "ХРАНИТЕЛЬ-0002",
        localPts: 1240,
        prizeCoins: 2450,
      },
      me: {
        userId: 1,
        nickname: "Игрок",
        localPts: 380,
        rank: 7,
        ptsToTop1: 861,
        isTop1: false,
      },
      top3: [],
    },
    almanac: {
      stampedSlots: 12,
      totalSlots: 108,
      progressPercent: 11.1,
      milestones: [
        {
          percent: 25,
          minSlots: 27,
          title: "Ученик архива",
          rewardCoins: 1000,
          rewardChakra: 500,
          rewardFuseBoosters: 0,
          frameKey: "almanac:apprentice",
          reached: false,
          claimed: false,
          claimable: false,
        },
        {
          percent: 50,
          minSlots: 54,
          title: "Хранитель архива",
          rewardCoins: 5000,
          rewardChakra: 500,
          rewardFuseBoosters: 0,
          frameKey: "almanac:archivist",
          reached: false,
          claimed: false,
          claimable: false,
        },
        {
          percent: 75,
          minSlots: 81,
          title: "Мастер знаний",
          rewardCoins: 10000,
          rewardChakra: 1000,
          rewardFuseBoosters: 0,
          frameKey: "almanac:master",
          reached: false,
          claimed: false,
          claimable: false,
        },
        {
          percent: 95,
          minSlots: 103,
          title: "Хранитель свода",
          rewardCoins: 20000,
          rewardChakra: 2000,
          rewardFuseBoosters: 0,
          frameKey: "almanac:vault",
          reached: false,
          claimed: false,
          claimable: false,
        },
        {
          percent: 100,
          minSlots: 108,
          title: "Архивариус Даксби",
          rewardCoins: 50000,
          rewardChakra: 5000,
          rewardFuseBoosters: 0,
          frameKey: "almanac:grand",
          reached: false,
          claimed: false,
          claimable: false,
        },
      ],
    },
    ladder: {
      stages: [
        {
          key: "SPARK",
          title: "Искра",
          order: 1,
          rewardCoins: 50,
          rewardChakra: 25,
          frameKey: "ladder:spark",
          requiredTasks: 3,
          totalTasks: 3,
          doneCount: 1,
          tasks: [
            { key: "GREEN_CARD", title: "Первая зелёная карта", done: false },
            {
              key: "WINS_3",
              title: "3 победы в PvP",
              done: true,
              progress: 3,
              target: 3,
            },
            { key: "FIRST_STAMP", title: "Первая печать альманаха", done: false },
          ],
          completed: false,
          claimed: false,
          claimable: false,
          unlocked: true,
        },
        {
          key: "CITY_GUARD",
          title: "Страж города",
          order: 2,
          rewardCoins: 200,
          rewardChakra: 75,
          frameKey: "ladder:city_guard",
          requiredTasks: 4,
          totalTasks: 4,
          doneCount: 0,
          tasks: [],
          completed: false,
          claimed: false,
          claimable: false,
          unlocked: false,
        },
        {
          key: "LEGEND",
          title: "Легенда сезона",
          order: 3,
          rewardCoins: 500,
          rewardChakra: 150,
          frameKey: "ladder:legend",
          requiredTasks: 3,
          totalTasks: 4,
          doneCount: 0,
          tasks: [],
          completed: false,
          claimed: false,
          claimable: false,
          unlocked: false,
        },
      ],
      currentStageKey: "SPARK",
      activeTitle: null,
      activeFrameKey: null,
      titles: [],
    },
  }
}
